import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getReservationsAPI } from "../../utils/ordersControlar";
import { RESERVATION_STATUS, dueReservationCount } from "./reservationUtils";

// 今日待處理預約的共用資料來源：首頁卡片、功能卡徽章、全域提醒都吃這一份，
// 避免每個元件各自輪詢。每 60 秒重抓一次，後端 WS updateReservation 會轉成
// reservation-updated 事件（GlobalStateContext）立即刷新。
const EMPTY = { reservations: [], nowMs: Date.now(), dueCount: 0, refresh: () => {} };
const TodayReservationsContext = createContext(EMPTY);

// enabled=false（尚未登入、kiosk 模式）時不輪詢，避免 401 與 iPad 多餘請求。
export const TodayReservationsProvider = ({ children, enabled = true }) => {
  const [reservations, setReservations] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      const response = await getReservationsAPI({
        date: dayjs().format("YYYY-MM-DD"),
        status: RESERVATION_STATUS.PENDING,
      });
      if (response?.error === 0) {
        setReservations(response.list || []);
      }
    } catch (error) {
      // 共用 API 已顯示錯誤，首頁摘要保留上一份資料即可。
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setReservations([]);
      return undefined;
    }
    refresh();
    const timer = setInterval(() => {
      setNowMs(Date.now());
      refresh();
    }, 60 * 1000);
    window.addEventListener("reservation-updated", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("reservation-updated", refresh);
    };
  }, [enabled, refresh]);

  const value = useMemo(() => ({
    reservations,
    nowMs,
    dueCount: dueReservationCount(reservations, nowMs),
    refresh,
  }), [reservations, nowMs, refresh]);

  return (
    <TodayReservationsContext.Provider value={value}>
      {children}
    </TodayReservationsContext.Provider>
  );
};

export const useTodayReservations = () => useContext(TodayReservationsContext);
